/* ============================================================
   INDSO — global.js
   Refactored from header.js + custom.js to drive server-rendered
   Liquid markup (sections render their own DOM; JS only wires up
   interactivity) plus Shopify AJAX cart.
   ============================================================ */
(function () {
  'use strict';

  const ready = (fn) =>
    document.readyState !== 'loading'
      ? fn()
      : document.addEventListener('DOMContentLoaded', fn);

  /* ===== MOBILE NAV DRAWER ===== */
  function initMobileNav() {
    const burger = document.getElementById('hamburger');
    const drawer = document.getElementById('mnav');
    const overlay = document.getElementById('mnavOverlay');
    const closeBtn = document.getElementById('mnavClose');
    if (!burger || !drawer || !overlay) return;

    const open = () => {
      drawer.classList.add('open');
      overlay.classList.add('open');
      burger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      drawer.classList.remove('open');
      overlay.classList.remove('open');
      burger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    };

    burger.addEventListener('click', open);
    overlay.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);
    drawer.querySelectorAll('.mnav-links a').forEach((a) => a.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    window.addEventListener('resize', () => { if (window.innerWidth > 820) close(); });
  }

  /* ===== AJAX CART DRAWER ===== */
  function initCart() {
    const cartBtn = document.getElementById('cartBtn');
    const drawer = document.getElementById('cartDrawer');
    const overlay = document.getElementById('cartOverlay');
    if (!drawer || !overlay) return; // page-based cart: let the link navigate

    const open = () => { drawer.classList.add('open'); overlay.classList.add('open'); document.body.style.overflow = 'hidden'; };
    const close = () => { drawer.classList.remove('open'); overlay.classList.remove('open'); document.body.style.overflow = ''; };

    if (cartBtn) {
      cartBtn.addEventListener('click', (e) => { e.preventDefault(); open(); });
    }
    overlay.addEventListener('click', close);
    const closeBtn = document.getElementById('cartClose');
    if (closeBtn) closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    // Re-render the drawer + bag count from /cart.js
    async function refresh(openAfter) {
      try {
        const res = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await res.json();
        updateCount(cart.item_count);
        await rebuildDrawer();
        if (openAfter) open();
      } catch (e) { /* noop */ }
    }

    function updateCount(count) {
      const el = document.getElementById('cartCount');
      if (!el) return;
      el.textContent = count;
      el.classList.toggle('cart-count--empty', count === 0);
    }

    // Pull a fresh server-rendered drawer so line items match Liquid exactly
    async function rebuildDrawer() {
      const res = await fetch(window.location.pathname + '?section_id=cart-fragment');
      if (!res.ok) return;
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const fresh = html.getElementById('cartDrawer');
      if (fresh) drawer.innerHTML = fresh.innerHTML;
      bindDrawer();
      const newClose = document.getElementById('cartClose');
      if (newClose) newClose.addEventListener('click', close);
    }

    async function changeQty(key, qty, itemEl) {
      // affected item shows a loader; repeats are blocked while in-flight
      if (itemEl) {
        if (itemEl.classList.contains('is-updating')) return;
        itemEl.classList.add('is-updating');
        if (!itemEl.querySelector('.citem-loader')) {
          const sp = document.createElement('span');
          sp.className = 'citem-loader';
          sp.setAttribute('aria-hidden', 'true');
          itemEl.appendChild(sp);
        }
      }
      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
        });
        if (!res.ok) throw new Error('cart change failed');
        const cart = await res.json();
        updateCount(cart.item_count);
        await rebuildDrawer(); // re-renders items; loader element goes with the old DOM
      } catch (err) {
        // graceful: clear the loading state so the user can retry
        if (itemEl) {
          itemEl.classList.remove('is-updating');
          const sp = itemEl.querySelector('.citem-loader');
          if (sp) sp.remove();
        }
      }
    }

    function bindDrawer() {
      drawer.querySelectorAll('[data-quantity-change]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const itemEl = btn.closest('[data-cart-item]');
          changeQty(btn.dataset.key, parseInt(btn.dataset.quantity, 10), itemEl);
        });
      });
    }
    bindDrawer();

    // Intercept "Add to Bag" forms (product cards + product page)
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"], .card-add-form, .product-form-form, .pdp-form');
      if (!form) return;
      e.preventDefault();

      // Per-button loading state (independent per card). Lock width to avoid shift.
      const btn = form.querySelector('[type="submit"]');
      if (btn && btn.classList.contains('is-loading')) return; // ignore repeat clicks
      let restore = null;
      if (btn) {
        const w = btn.getBoundingClientRect().width;
        const original = btn.innerHTML;
        btn.style.minWidth = w + 'px';
        btn.classList.add('is-loading');
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.innerHTML = '<span class="btn-spinner" aria-hidden="true"></span>';
        restore = () => {
          btn.classList.remove('is-loading');
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          btn.style.minWidth = '';
          btn.innerHTML = original;
        };
      }

      const formData = new FormData(form);
      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: formData,
        });
        if (!res.ok) throw new Error('add failed');
        await refresh(true);
        if (restore) restore();
      } catch (err) {
        if (restore) restore();
        form.submit();
      }
    });

    // expose for other scripts
    window.INDSOCart = { open, close, refresh };
  }

  /* ===== HERO SLIDESHOW ===== */
  function initSlideshow() {
    const root = document.querySelector('[data-slideshow]');
    if (!root) return;
    const slides = root.querySelectorAll('.hslide');
    const dots = root.querySelectorAll('.hdots button');
    if (slides.length < 2) return;
    const speed = (parseInt(root.dataset.autoplay, 10) || 7) * 1000;
    let cur = 0, timer;

    function go(i) {
      cur = (i + slides.length) % slides.length;
      slides.forEach((el, n) => {
        el.classList.toggle('on', n === cur);
        const v = el.querySelector('video');
        if (v) { n === cur ? v.play().catch(() => {}) : v.pause(); }
      });
      dots.forEach((d, n) => d.classList.toggle('on', n === cur));
    }
    function reset() { clearInterval(timer); timer = setInterval(() => go(cur + 1), speed); }

    dots.forEach((d, i) => d.addEventListener('click', () => { go(i); reset(); }));
    const next = root.querySelector('[data-slide-next]');
    const prev = root.querySelector('[data-slide-prev]');
    if (next) next.addEventListener('click', () => { go(cur + 1); reset(); });
    if (prev) prev.addEventListener('click', () => { go(cur - 1); reset(); });
    go(0); reset();
  }

  /* ===== SPLIT SLIDER ===== */
  function initSplitSliders() {
    document.querySelectorAll('[data-split-slider]').forEach((root) => {
      const track = root.querySelector('[data-slides]');
      const slides = track ? track.children : [];
      if (slides.length < 2) return;
      const countEl = root.querySelector('[data-slide-count]');
      const total = slides.length;
      let cur = 0, timer;
      const pad = (n) => String(n).padStart(2, '0');

      function go(i) {
        cur = (i + total) % total;
        track.style.transform = `translateX(-${cur * 100}%)`;
        if (countEl) countEl.textContent = `${pad(cur + 1)} / ${pad(total)}`;
      }
      root.querySelector('[data-slide-next]')?.addEventListener('click', () => { go(cur + 1); reset(); });
      root.querySelector('[data-slide-prev]')?.addEventListener('click', () => { go(cur - 1); reset(); });
      function reset() { clearInterval(timer); timer = setInterval(() => go(cur + 1), 6000); }
      go(0); reset();
      root.addEventListener('mouseenter', () => clearInterval(timer));
      root.addEventListener('mouseleave', reset);
    });
  }

  /* ===== TABS (Shop by Type) ===== */
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach((root) => {
      const tabs = root.querySelectorAll('.tab');
      const panels = root.querySelectorAll('[data-tab-panel]');
      tabs.forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.tab;
          tabs.forEach((b) => {
            const active = b === btn;
            b.classList.toggle('on', active);
            b.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          panels.forEach((p) => p.classList.toggle('on', p.dataset.tabPanel === id));
        });
      });
    });
  }

  /* ===== VIDEO CAROUSEL (click to play, one at a time) ===== */
  function initVideoRails() {
    document.querySelectorAll('[data-video-rail]').forEach((rail) => {
      const cards = rail.querySelectorAll('.vcard');
      cards.forEach((card) => {
        const vid = card.querySelector('video');
        if (!vid) return;
        card.addEventListener('click', () => {
          if (vid.paused) {
            cards.forEach((c) => {
              if (c !== card) {
                const ov = c.querySelector('video');
                if (ov) ov.pause();
                c.classList.remove('playing');
              }
            });
            vid.play(); card.classList.add('playing');
          } else { vid.pause(); card.classList.remove('playing'); }
        });
        vid.addEventListener('pause', () => card.classList.remove('playing'));
        vid.addEventListener('play', () => card.classList.add('playing'));
      });

      // Desktop arrow navigation (mobile keeps native swipe)
      const wrap = rail.closest('.vrail-wrap');
      if (!wrap) return;
      const prev = wrap.querySelector('[data-vrail-prev]');
      const next = wrap.querySelector('[data-vrail-next]');
      if (!prev || !next) return;

      function step() {
        const first = rail.querySelector('.vcard');
        if (!first) return rail.clientWidth * 0.8;
        const gap = parseInt(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || '18', 10) || 18;
        return first.getBoundingClientRect().width + gap;
      }
      function updateArrows() {
        const max = rail.scrollWidth - rail.clientWidth - 1;
        prev.disabled = rail.scrollLeft <= 0;
        next.disabled = rail.scrollLeft >= max;
        // hide both if nothing to scroll
        const noScroll = rail.scrollWidth <= rail.clientWidth + 2;
        wrap.classList.toggle('vrail-wrap--static', noScroll);
      }
      prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
      rail.addEventListener('scroll', () => { window.requestAnimationFrame(updateArrows); });
      window.addEventListener('resize', updateArrows);
      updateArrows();
    });
  }

  /* ===== TESTIMONIAL / REVIEW CAROUSEL (side arrows + optional dots) ===== */
  function initTestimonials() {
    document.querySelectorAll('[data-testimonials]').forEach((root) => {
      const grid = root.querySelector('[data-testi-grid]');
      if (!grid) return;
      const dotsWrap = root.querySelector('[data-testi-dots]'); // optional
      const cards = Array.from(grid.querySelectorAll('.quote, .rev'));
      if (!cards.length) return;

      const prevBtn = root.querySelector('[data-testi-prev]');
      const nextBtn = root.querySelector('[data-testi-next]');

      const currentIndex = () => {
        const mid = grid.scrollLeft + grid.clientWidth / 2;
        let active = 0, best = Infinity;
        cards.forEach((c, i) => {
          const center = c.offsetLeft + c.offsetWidth / 2;
          const d = Math.abs(center - mid);
          if (d < best) { best = d; active = i; }
        });
        return active;
      };
      const goTo = (i) => {
        const idx = Math.max(0, Math.min(cards.length - 1, i));
        cards[idx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      };

      if (dotsWrap) {
        cards.forEach((c, i) => {
          const b = document.createElement('button');
          b.setAttribute('aria-label', 'Review ' + (i + 1));
          if (i === 0) b.className = 'on';
          b.addEventListener('click', () => goTo(i));
          dotsWrap.appendChild(b);
        });
      }

      if (prevBtn) prevBtn.addEventListener('click', () => goTo(currentIndex() - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(currentIndex() + 1));

      // arrows show only when the track overflows (more items than fit in view)
      const isScrollable = () => grid.scrollWidth - grid.clientWidth > 1;

      const update = () => {
        const scrollable = isScrollable();
        root.classList.toggle('is-scrollable', scrollable);
        const active = currentIndex();
        if (dotsWrap) {
          dotsWrap.querySelectorAll('button').forEach((b, i) => b.classList.toggle('on', i === active));
        }
        if (prevBtn) prevBtn.disabled = !scrollable || active === 0;
        if (nextBtn) nextBtn.disabled = !scrollable || active === cards.length - 1;
      };

      let t;
      grid.addEventListener('scroll', () => {
        clearTimeout(t);
        t = setTimeout(update, 60);
      });
      let rt;
      window.addEventListener('resize', () => {
        clearTimeout(rt);
        rt = setTimeout(update, 150);
      });
      update();
    });
  }

  ready(() => {
    if (!window.Shopify) window.Shopify = {};
    if (!window.Shopify.routes) window.Shopify.routes = { root: '/' };
    initMobileNav();
    initCart();
    initSlideshow();
    initSplitSliders();
    initTabs();
    initVideoRails();
    initTestimonials();
  });
})();
